import React from 'react';
import i18n from '../../../../i18n'; // TODO: point this at your actual i18n instance/config
import { orderStatus } from '../Wallet/services/walletService';
import { CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import '../../../../styles/components/payment-result.scss';

/**
 * Rendered at the route the backend redirects to after processing the
 * SmartPay callback, e.g. /payment/result/:orderId
 *
 * react-router v5: register as <Route path="/payment/result/:orderId" component={PaymentResult} />
 *   -> orderId is available at this.props.match.params.orderId
 *
 * react-router v6 (no match prop passed to class components): wrap it —
 *   function PaymentResultRoute() {
 *     const params = useParams();
 *     return <PaymentResult match={{ params }} />;
 *   }
 *   and route to <PaymentResultRoute /> instead.
 *
 * The status query param in the URL is only used for the first paint;
 * the real, trusted status always comes from GET /payment/status/:orderId.
 */

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

function getTypeLabels() {
  return {
    wallet_recharge: {
      success: i18n.t('wallet_recharge_success'),
      action: { label: i18n.t('go_to_wallet'), href: '/wallet-manage' },
    },
    auction_enrollment: {
      success: i18n.t('auction_enrollment_success'),
      action: { label: i18n.t('view_auction'), href: null }, // filled in from txn.reference_id if available
    },
    auction_payment: {
      success: i18n.t('auction_payment_success'),
      action: { label: i18n.t('go_to_auction'), href: null },
    },
  };
}

function getFailedSubtitleByType() {
  return {
    wallet_recharge: i18n.t('wallet_recharge_failed_subtitle'),
    auction_enrollment: i18n.t('auction_enrollment_failed_subtitle'),
    auction_payment: i18n.t('auction_payment_failed_subtitle'),
  };
}

function getFailedSubtitle(type) {
  return getFailedSubtitleByType()[type] || i18n.t('payment_failed_default_subtitle');
}

function getBackLink(txn) {
  switch (txn?.type) {
    case 'wallet_recharge':
      return '/wallet';
    case 'auction_enrollment':
      return txn?.reference_id ? `/auction-details/${txn.reference_id}` : '/auction';
    case 'auction_payment':
      return '/mzadcom-payment';
    default:
      return '/';
  }
}

function getBackLabel(type) {
  switch (type) {
    case 'wallet_recharge':
      return i18n.t('back_to_wallet');
    case 'auction_enrollment':
      return i18n.t('back_to_auction');
    case 'auction_payment':
      return i18n.t('back_to_auction_payments');
    default:
      return i18n.t('back_to_home');
  }
}

function formatAmount(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString('en-OM', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

class PaymentResult extends React.Component {
  constructor(props) {
    super(props);
    const path = window.location.pathname.replace(/\/+$/, ''); // strip trailing slash(es)
    const orderId = path.substring(path.lastIndexOf('/') + 1);
    this.state = {
      phase: 'checking', // checking | success | failed | error
      txn: null,
      errorMessage: null,
      retryCount: 0,
      orderId,
    };
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    this.verifyStatus();
  }

  componentWillUnmount() {
    this._mounted = false;
    clearTimeout(this._retryTimer);
  }

  verifyStatus = async () => {
    const { orderId } = this.state;

    if (!orderId) {
      this.setState({ phase: 'error', errorMessage: i18n.t('no_order_reference_provided') });
      return;
    }

    try {
      const data = await orderStatus(orderId);
      console.log(data, 'res summary');

      if (!this._mounted) return;

      const isSuccess = Boolean(data.is_success);

      this.setState({
        phase: isSuccess ? 'success' : 'failed',
        txn: data,
      });
    } catch (err) {
      console.log(err, 'err');

      if (!this._mounted) return;

      const { retryCount } = this.state;

      if (retryCount < MAX_RETRIES) {
        this._retryTimer = setTimeout(() => {
          if (!this._mounted) return;
          this.setState((prev) => ({ retryCount: prev.retryCount + 1 }), this.verifyStatus);
        }, RETRY_DELAY_MS);
        return;
      }

      this.setState({
        phase: 'error',
        errorMessage: err.response?.data?.message || i18n.t('verify_payment_error_default_message'),
      });
    }
  };

  handleManualRetry = () => {
    this.setState({ phase: 'checking', errorMessage: null, retryCount: 0 }, this.verifyStatus);
  };

  getTypeInfo(type) {
    return getTypeLabels()[type] || { success: i18n.t('payment_processed_default'), action: null };
  }

  renderChecking() {
    const { retryCount } = this.state;
    return (
      <div className="pr-card pr-card--pending" role="status" aria-live="polite">
        <div className="pr-icon pr-icon--spin">
          <Loader2 size={40} strokeWidth={2} />
        </div>
        <h1 className="pr-title">{i18n.t('verifying_payment_title')}</h1>
        <p className="pr-subtitle">
          {retryCount > 0
            ? i18n.t('verifying_payment_retry', {
                current: retryCount + 1,
                total: MAX_RETRIES + 1,
              })
            : i18n.t('verifying_payment_subtitle')}
        </p>
        <div className="pr-progress" aria-hidden="true">
          <div className="pr-progress__bar" />
        </div>
      </div>
    );
  }

  renderSuccess() {
    const { txn } = this.state;
    const typeInfo = this.getTypeInfo(txn.type);

    return (
      <div className="pr-card pr-card--success">
        <div className="pr-icon pr-icon--success">
          <CheckCircle2 size={40} strokeWidth={2} />
        </div>
        <h1 className="pr-title">{i18n.t('payment_successful_title')}</h1>
        <p className="pr-subtitle">{typeInfo.success}</p>

        <dl className="pr-summary">
          {txn.tracking_id && (
            <div className="pr-summary__row">
              <dt>{i18n.t('reference_label')}</dt>
              <dd>{txn.tracking_id}</dd>
            </div>
          )}
          {txn.order_id && (
            <div className="pr-summary__row">
              <dt>{i18n.t('order_id_label')}</dt>
              <dd>{txn.order_id}</dd>
            </div>
          )}
          {txn.amount != null && (
            <div className="pr-summary__row">
              <dt>{i18n.t('amount_label')}</dt>
              <dd>
                {i18n.t('currency_omr')} {formatAmount(txn.amount)}
              </dd>
            </div>
          )}
        </dl>

        <div className="pr-actions">
          {/* <a href="/" className="pr-btn pr-btn--primary">
            {i18n.t('back_to_home')} <ArrowRight size={16} />
          </a> */}
          <a href={getBackLink(txn)} className="pr-btn pr-btn--primary">
            {getBackLabel(txn?.type)}
          </a>
        </div>
      </div>
    );
  }

  renderFailed() {
    const { txn } = this.state;

    return (
      <div className="pr-card pr-card--failed">
        <div className="pr-icon pr-icon--failed">
          <XCircle size={40} strokeWidth={2} />
        </div>
        <h1 className="pr-title">{i18n.t('payment_not_completed_title')}</h1>
        <p className="pr-subtitle">{getFailedSubtitle(txn?.type)}</p>

        <dl className="pr-summary">
          {txn?.order_id && (
            <div className="pr-summary__row">
              <dt>{i18n.t('order_id_label')}</dt>
              <dd>{txn.order_id}</dd>
            </div>
          )}
          <div className="pr-summary__row">
            <dt>{i18n.t('status_label')}</dt>
            <dd>{txn.status_label}</dd>
          </div>
        </dl>

        <div className="pr-actions">
          <a href={getBackLink(txn)} className="pr-btn pr-btn--ghost">
            {getBackLabel(txn?.type)}
          </a>

          {/* <button type="button" className="pr-btn pr-btn--primary" onClick={this.handleManualRetry}>
            <RefreshCw size={16} /> {i18n.t('try_again')}
          </button> */}
        </div>
      </div>
    );
  }

  renderError() {
    const { errorMessage } = this.state;

    return (
      <div className="pr-card pr-card--error">
        <div className="pr-icon pr-icon--error">
          <AlertTriangle size={40} strokeWidth={2} />
        </div>
        <h1 className="pr-title">{i18n.t('verify_payment_error_title')}</h1>
        <p className="pr-subtitle">{errorMessage}</p>

        <div className="pr-actions">
          <a href="/" className="pr-btn pr-btn--ghost">
            {i18n.t('back_to_home')}
          </a>
          <button type="button" className="pr-btn pr-btn--primary" onClick={this.handleManualRetry}>
            <RefreshCw size={16} /> {i18n.t('check_again')}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { phase } = this.state;

    return (
      <div className="pr-page">
        <div className="pr-container">
          {phase === 'checking' && this.renderChecking()}
          {phase === 'success' && this.renderSuccess()}
          {phase === 'failed' && this.renderFailed()}
          {phase === 'error' && this.renderError()}
        </div>
      </div>
    );
  }
}

export default PaymentResult;
